import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", labelAr: "الرئيسية", href: "/" },
  { label: "Services", labelAr: "خدماتنا", href: "/services" },
  { label: "About", labelAr: "عن الصالون", href: "/#about" },
  { label: "Team", labelAr: "فريقنا", href: "/#team" },
  { label: "Gallery", labelAr: "معرض", href: "/gallery" },
  { label: "Contact", labelAr: "اتصل بنا", href: "/#contact" },
];

const WA_LINK = "https://api.whatsapp.com/send?phone=96892102051&text=Hello%20Blue%20Moon%20Beauty%20Salon%20%26%20Spa%2C%20I%20would%20like%20more%20information%20about%20your%20services.";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [location]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href === "/") {
      e.preventDefault();
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
      setMenuOpen(false);
      return;
    }
    if (href.startsWith("/#")) {
      e.preventDefault();
      const id = href.replace("/#", "");
      setMenuOpen(false);
      if (location.pathname === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate("/#" + id);
      }
      return;
    }
    e.preventDefault();
    navigate(href);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMenuOpen(false);
  };

  const textColor = scrolled ? "text-[#1a2744]" : "text-white";
  const borderColor = scrolled ? "border-[#1a2744]" : "border-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-20">
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => handleNavClick(e, "/")}
          className="cursor-pointer flex flex-col leading-tight"
        >
          <span
            className={`font-serif tracking-[0.2em] text-base sm:text-lg font-semibold uppercase transition-colors duration-300 ${textColor}`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            BLUE MOON
          </span>
          <span
            className={`text-[10px] sm:text-xs tracking-[0.25em] font-light uppercase transition-colors duration-300 ${textColor} opacity-80`}
            style={{ fontFamily: "'Noto Naskh Arabic', serif" }}
          >
            صالون وسبا القمر الأزرق
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`text-xs tracking-widest uppercase font-light transition-colors duration-300 hover:opacity-70 whitespace-nowrap cursor-pointer ${textColor}`}
            >
              {link.label}
            </a>
          ))}
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className={`whitespace-nowrap cursor-pointer border ${borderColor} ${textColor} text-xs tracking-widest uppercase px-4 py-2 hover:bg-[#c6a864] hover:text-white hover:border-[#c6a864] transition-all duration-300`}
          >
            Book Now
          </a>
        </nav>

        {/* Mobile Actions */}
        <div className="flex items-center gap-3 lg:hidden">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className={`w-8 h-8 flex items-center justify-center ${textColor}`}
          >
            <i className="ri-whatsapp-line text-xl"></i>
          </a>
          <button
            className="cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i
              className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl transition-colors duration-300 ${textColor}`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-[#ece8e0] px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="flex justify-between items-center text-sm text-[#1a2744] font-light hover:text-[#c6a864] transition-colors cursor-pointer border-b border-[#f4f1ea] pb-3"
            >
              <span className="tracking-widest uppercase text-xs">{link.label}</span>
              <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }} className="text-sm text-[#1a2744]/70">
                {link.labelAr}
              </span>
            </a>
          ))}
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer bg-[#25D366] text-white text-xs tracking-widest uppercase px-5 py-3 hover:bg-[#20ba5a] transition-colors duration-300"
          >
            <i className="ri-whatsapp-line text-base"></i>
            WhatsApp Us
          </a>
        </div>
      )}
    </header>
  );
}
